/**
 * RunsTable — a task's pushed runs, newest first (API order).
 *
 * Rows navigate to the run detail page. Duration is derived from
 * started/finished timestamps when the pipeline reported both.
 *
 * Reference runs (random, a baseline, SOTA) are listed here too, badged —
 * they're excluded from every score rollup and draw as dotted lines on the
 * group chart, so the badge explains why they don't move the numbers.
 */

import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'

import { RunStatusBadge } from '@/components/evals/RunStatusBadge'
import { formatDuration, formatScore } from '@/components/evals/format'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { EvalRunSummary } from '@/api/generated/model'

interface RunsTableProps {
  groupSlug: string
  taskSlug: string
  runs: EvalRunSummary[]
}

function DateCell({ run }: { run: EvalRunSummary }): React.JSX.Element {
  const date = new Date(run.finished_at ?? run.created_at)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default text-sm text-foreground">
          {formatDistanceToNow(date, { addSuffix: true })}
        </span>
      </TooltipTrigger>
      <TooltipContent>{date.toLocaleString()}</TooltipContent>
    </Tooltip>
  )
}

export function RunsTable({ groupSlug, taskSlug, runs }: RunsTableProps): React.JSX.Element {
  const navigate = useNavigate()

  if (runs.length === 0) {
    return (
      <EmptyState title="No runs yet" description="Scores will appear here after the first run." />
    )
  }

  return (
    <div className="rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Eval dataset</TableHead>
            <TableHead>Variant</TableHead>
            <TableHead className="text-right">Score</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => (
            <TableRow
              key={run.id}
              className="cursor-pointer"
              onClick={() => {
                navigate(`/evals/groups/${groupSlug}/tasks/${taskSlug}/runs/${run.id}`)
              }}
            >
              <TableCell>
                <DateCell run={run} />
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <span className="truncate">{run.model_slug}</span>
                  {run.reference_label !== '' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="shrink-0">
                          {run.reference_label}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        Reference run. It is drawn as a dotted line on the group chart and
                        excluded from the scores.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
              </TableCell>
              <TableCell className="max-w-48 truncate font-mono text-xs text-muted-foreground">
                {run.dataset_eval || '—'}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {run.variant || '—'}
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums">
                {formatScore(run.overall_score)}
              </TableCell>
              <TableCell>
                <RunStatusBadge status={run.status} />
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                {formatDuration(run.started_at, run.finished_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
